/**
 * New Conversation Dialog
 *
 * Dialog for creating new direct messages or channels.
 * Supports selecting users and configuring channel settings.
 */

import { useState } from 'react'
import { router } from '@inertiajs/react'
import { MessageSquareIcon, UsersIcon, SearchIcon } from 'lucide-react'
import axios from 'axios'
import { toast } from 'sonner'
import authService from '@/lib/auth'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

interface User {
  id: number
  name: string
  email: string
}

interface NewConversationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  availableUsers: User[]
  currentUser: User
}

export function NewConversationDialog({
  open,
  onOpenChange,
  availableUsers,
  currentUser,
}: NewConversationDialogProps) {
  // Direct Message state
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [dmSearchTerm, setDmSearchTerm] = useState('')

  // Channel state
  const [channelName, setChannelName] = useState('')
  const [channelDescription, setChannelDescription] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [selectedMembers, setSelectedMembers] = useState<User[]>([])
  const [channelSearchTerm, setChannelSearchTerm] = useState('')

  // UI state
  const [activeTab, setActiveTab] = useState<'dm' | 'channel'>('dm')
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filteredUsersForDM = availableUsers
    .filter(user => user.id !== currentUser.id)
    .filter(user =>
      user.name.toLowerCase().includes(dmSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(dmSearchTerm.toLowerCase())
    )

  const filteredUsersForChannel = availableUsers
    .filter(user => user.id !== currentUser.id)
    .filter(user =>
      !selectedMembers.find(m => m.id === user.id) &&
      (user.name.toLowerCase().includes(channelSearchTerm.toLowerCase()) ||
       user.email.toLowerCase().includes(channelSearchTerm.toLowerCase()))
    )

  const handleCreateDM = async () => {
    if (!selectedUser) {
      toast.error('Please select a user to message')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      const response = await axios.post('/messages/conversations', {
        conversation_type: 'direct_message',
        user_ids: [selectedUser.id]
      }, {
        headers: authService.authHeaders()
      })

      const { conversation } = response.data
      toast.success('Conversation created successfully')
      onOpenChange(false)
      router.visit(`/messages/conversations/${conversation.id}`)
    } catch (err: any) {
      const errorMessage = err.response?.data?.errors?.join(', ') || 'Failed to create conversation'
      toast.error(errorMessage)
      setError(errorMessage)
    } finally {
      setIsCreating(false)
    }
  }

  const handleCreateChannel = async () => {
    if (!channelName.trim()) {
      toast.error('Please enter a channel name')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      const response = await axios.post('/messages/conversations', {
        conversation_type: 'channel',
        name: channelName,
        description: channelDescription,
        is_private: isPrivate,
        user_ids: selectedMembers.map(u => u.id)
      }, {
        headers: authService.authHeaders()
      })

      const { conversation } = response.data
      toast.success('Channel created successfully')
      onOpenChange(false)
      router.visit(`/messages/conversations/${conversation.id}`)
    } catch (err: any) {
      const errorMessage = err.response?.data?.errors?.join(', ') || 'Failed to create channel'
      toast.error(errorMessage)
      setError(errorMessage)
    } finally {
      setIsCreating(false)
    }
  }

  const handleClose = () => {
    setSelectedUser(null)
    setDmSearchTerm('')
    setChannelName('')
    setChannelDescription('')
    setIsPrivate(false)
    setSelectedMembers([])
    setChannelSearchTerm('')
    setError(null)
    setActiveTab('dm')
    onOpenChange(false)
  }

  const toggleMember = (user: User) => {
    setSelectedMembers(prev =>
      prev.find(m => m.id === user.id)
        ? prev.filter(m => m.id !== user.id)
        : [...prev, user]
    )
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>New Conversation</DialogTitle>
          <DialogDescription>
            Start a direct message or create a team channel
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'dm' | 'channel')} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="dm">
              <MessageSquareIcon className="h-4 w-4 mr-2" />
              Direct Message
            </TabsTrigger>
            <TabsTrigger value="channel">
              <UsersIcon className="h-4 w-4 mr-2" />
              Channel
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dm" className="flex-1 overflow-hidden flex flex-col space-y-4">
            <div className="space-y-2">
              <Label>Message User</Label>
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={dmSearchTerm}
                  onChange={(e) => setDmSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto border rounded-md">
              <div className="space-y-1 p-2">
                {filteredUsersForDM.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No users found
                  </div>
                ) : (
                  filteredUsersForDM.map(user => (
                    <button
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors ${
                        selectedUser?.id === user.id ? 'bg-accent' : ''
                      }`}
                    >
                      <Avatar>
                        <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                      </Avatar>
                      <div className="text-left flex-1">
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {error && (
              <div className="text-sm text-destructive">{error}</div>
            )}
          </TabsContent>

          <TabsContent value="channel" className="flex-1 overflow-hidden flex flex-col space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="channel-name">Channel Name</Label>
                <Input
                  id="channel-name"
                  placeholder="team-chat"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="channel-description">Description (optional)</Label>
                <Textarea
                  id="channel-description"
                  placeholder="What's this channel about?"
                  value={channelDescription}
                  onChange={(e) => setChannelDescription(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="private-channel">Private Channel</Label>
                <Switch
                  id="private-channel"
                  checked={isPrivate}
                  onCheckedChange={setIsPrivate}
                />
              </div>

              <div className="space-y-2">
                <Label>Add Members</Label>
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users to add..."
                    value={channelSearchTerm}
                    onChange={(e) => setChannelSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {selectedMembers.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedMembers.map(member => (
                    <div
                      key={member.id}
                      className="flex items-center gap-2 bg-accent px-3 py-1 rounded-full text-sm"
                    >
                      {member.name}
                      <button
                        onClick={() => toggleMember(member)}
                        className="hover:text-destructive"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto border rounded-md">
              <div className="space-y-1 p-2">
                {filteredUsersForChannel.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    {selectedMembers.length > 0 ? 'No more users to add' : 'No users found'}
                  </div>
                ) : (
                  filteredUsersForChannel.map(user => (
                    <button
                      key={user.id}
                      onClick={() => toggleMember(user)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                    >
                      <Avatar>
                        <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                      </Avatar>
                      <div className="text-left flex-1">
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {error && (
              <div className="text-sm text-destructive">{error}</div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          {activeTab === 'dm' ? (
            <Button
              onClick={handleCreateDM}
              disabled={!selectedUser || isCreating}
            >
              {isCreating ? 'Creating...' : 'Start Conversation'}
            </Button>
          ) : (
            <Button
              onClick={handleCreateChannel}
              disabled={!channelName.trim() || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create Channel'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
